import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Dropdown as AntdDropdown } from 'antd';
import { withRouter, NavLink } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

// Import styles
import {
	ItemStyled,
	NavDropdownStyled,
	Text,
	ButtonStyled,
	IconDown,
	MenuStyled
} from './styles';

// Import components
import Drawers from './Drawers';

// Import helpers
import { getTypeTranslation, getSearchCategory } from 'helpers';
import { sendGTMEvent } from 'helpers/tracking';

// Import variables
import { APP_PROJECT_TYPES, ROUTES_CORE_PATHS } from 'helpers/variables';
import {
	GTM_TRACKING_EVENTS,
	FORM_GTM_ACTION,
	GLOBAL_GTM_SEGMENTATION
} from 'helpers/tracking';
import useUserAgent from 'components/utilities/hooks/user_agent/useUserAgent';

const { CLICK_HEADER_MENU } = GTM_TRACKING_EVENTS;
const { TV_SMART } = APP_PROJECT_TYPES;
const { PAYMENTS, HOME, FAVOURITES, SEARCH } = ROUTES_CORE_PATHS;

const NavDropdown = ({
	isMobile = false,
	links,
	path = '',
	location: { pathname },
	categories,
	isMainNav = false,
	t
}) => {
	// Get main path
	const paths = pathname.split('/');
	const corePath = paths[1];
	const corePathTranslation = getTypeTranslation(paths[1]);
	const mainPath = paths[2];

	const { isMobile: mobileAgent } = useUserAgent();
	const [slug, setSlug] = React.useState('');

	// Get slug name
	const mainSlug = paths.pop();

	const matchSlug = React.useCallback(
		(slug) => {
			// Convert categories to object with name values
			const categorieConverted = categories.reduce((prev, next) => {
				prev[next.slug] = next.name;
				return prev;
			}, {});
			// If there is a slug return name, else return default text
			return categorieConverted[slug] || t('common_general_all');
		},
		/*eslint-disable-next-line  */
		[categories]
	);

	React.useEffect(() => {
		// Set path slug with matched name
		setSlug(matchSlug(mainSlug));
	}, [mainSlug, matchSlug]);

	// Create link path
	const handleCreatePath = (path, link) => {
		const linkSlug = (link.slug && getLinkSlug(link.slug)) || '';
		return `${path || link.path}${linkSlug && `/${linkSlug}`}`;
	};

	// check if user is on home/channels/epg page
	const isChannelsDropdown =
		path === `/tv/${getTypeTranslation('channels')}` ||
		path === '/tv/epg' ||
		path === '/tv' ||
		path === `/tv/telewizja`;

	const isSearchPage = corePathTranslation === 'search';

	// Get translated slug
	const getLinkSlug = (slug) => {
		if (isChannelsDropdown) {
			return slug;
		}

		return isSearchPage ? getSearchCategory(slug) : getTypeTranslation(slug);
	};

	const appName = TV_SMART.toLowerCase();

	// Create nav item text
	const handleNavItemText = ({ name, translationSection }) => {
		const navItem = name === 'home' ? `home_${appName}` : name;
		return isMainNav ? t(`common_${translationSection}_${navItem}`) : navItem;
	};

	// Create button text
	const handleButtonText = () => {
		if (isMainNav) {
			const navItem = isSearchPage ? SEARCH : mainPath;
			const itemTranslated =
				getTypeTranslation(navItem) ?? getTypeTranslation(corePath);
			const addAppName = navItem === HOME || navItem === PAYMENTS;
			const slug = addAppName ? `${itemTranslated}_${appName}` : itemTranslated;
			return t([`common_${links[0]?.translationSection}_${slug}`, corePath]);
		} else if (isSearchPage) {
			const paths = pathname.split('/');
			return t(`search_dropdown_${getSearchCategory(paths[3])}`);
		} else if (pathname.includes(FAVOURITES)) {
			return t(`common_header_nav_favourites`);
		}

		return slug || t('common_general_all');
	};

	const handleNavLinkClick = (link) => {
		const text = handleNavItemText(link);
		const url = handleCreatePath(path, link);

		sendGTMEvent({
			event: CLICK_HEADER_MENU,
			customEventData: {
				action: FORM_GTM_ACTION,
				segmentation: GLOBAL_GTM_SEGMENTATION,
				page_url: pathname,
				url: { url },
				text
			}
		});
	};

	const dropdownItems = (
		<MenuStyled isMobile={isMobile}>
			{links.map((link) => (
				<ItemStyled
					key={link.id}
					modifiers={link.itemModifiers}
					isMobile={isMobile}
				>
					<NavLink
						exact={!isMainNav}
						to={handleCreatePath(path, link)}
						activeClassName="active-drop-nav"
						onClick={() => handleNavLinkClick(link)}
					>
						{/* Create nav item text */}
						{handleNavItemText(link)}
					</NavLink>
				</ItemStyled>
			))}
			{isMobile && <Drawers />}
		</MenuStyled>
	);

	return (
		<>
			{isMobile ? (
				dropdownItems
			) : (
				<NavDropdownStyled>
					{!isMainNav && !mobileAgent && (
						<Text>{t('common_nav_category')}</Text>
					)}
					<AntdDropdown overlay={dropdownItems} trigger={['click']}>
						<ButtonStyled width="20rem" modifiers={['dropdown']}>
							{/* Create button text */}
							{handleButtonText()}
							<IconDown />
						</ButtonStyled>
					</AntdDropdown>
				</NavDropdownStyled>
			)}
		</>
	);
};

NavDropdown.propTypes = {
	links: PropTypes.array.isRequired,
	path: PropTypes.string,
	categories: PropTypes.array.isRequired,
	isMainNav: PropTypes.bool,
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired
	}),
	isMobile: PropTypes.bool
};

const mapStateToProps = ({ category: { data } }) => ({ categories: data });

export default compose(
	connect(mapStateToProps),
	withRouter,
	withTranslation()
)(React.memo(NavDropdown));
